import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Sparkles, Pencil, Trash2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useCalendars, useCalEvents, useCalMonth, useCalYear, useInvalidate } from "@/lib/queries";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { MonthGrid } from "./MonthGrid";
import { YearGrid } from "./YearGrid";
import { DayDialog } from "./DayDialog";
import { CalEventDialog } from "./CalEventDialog";
import { CalendarDialog } from "./CalendarDialog";
import { AIDraftDialog } from "./AIDraftDialog";
import { IntersectionsPanel } from "./IntersectionsPanel";
import { ConverterPanel } from "./ConverterPanel";
import { CalEventList } from "./CalEventList";

const VIEWS = [["month", "Month"], ["year", "Year"], ["convert", "Converter"], ["intersect", "Intersections"]];
const fmt = (n) => (Math.round(n * 100) / 100).toString();

function CalInfo({ cal, timelines }) {
  const tl = timelines.find((t) => t.id === cal.link?.timeline_id);
  const bits = cal.kind === "gregorian" ? ["Reference clock", `${cal.year_length}-day year`]
    : [`${fmt(cal.day_hours)}h day`, `${cal.year_length}-day year`, cal.rate !== 1 && `time rate ${fmt(cal.rate)}×`, `1 day = ${fmt(cal.day_length_earth_days)} Earth days`];
  if (cal.moons?.length) bits.push(`${cal.moons.length} moon${cal.moons.length > 1 ? "s" : ""}`);
  if (tl) bits.push(`linked to ${tl.name}`);
  return <p className="mb-5 text-sm text-[#A8A090]" data-testid="calendar-info">{bits.filter(Boolean).join(" · ")}{cal.description && <span className="block italic">{cal.description}</span>}</p>;
}

function Nav({ cal, cursor, setCursor, view }) {
  const n = cal.month_names.length;
  const [yearText, setYearText] = useState(String(cursor.year));
  useEffect(() => setYearText(String(cursor.year)), [cursor.year]);
  const step = (dir) => {
    if (view === "year") return setCursor({ ...cursor, year: cursor.year + dir });
    const m = cursor.month + dir;
    setCursor({ year: cursor.year + (m < 0 ? -1 : m >= n ? 1 : 0), month: (m + n) % n });
  };
  const commitYear = () => { const y = parseInt(yearText, 10); if (!Number.isNaN(y)) setCursor({ ...cursor, year: y }); };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="cal-nav">
      <Button size="icon" variant="ghost" onClick={() => step(-1)} data-testid="cal-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
      {view === "month" && (
        <Select value={String(cursor.month)} onValueChange={(v) => setCursor({ ...cursor, month: parseInt(v, 10) })}>
          <SelectTrigger className="h-10 w-44 font-display text-lg" data-testid="cal-month-select"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">{cal.month_names.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <Input value={yearText} onChange={(e) => setYearText(e.target.value)} onBlur={commitYear} onKeyDown={(e) => e.key === "Enter" && commitYear()}
        className="h-10 w-28 font-mono-lf" data-testid="cal-year-input" />
      {cal.era && <span className="text-sm text-[#A8A090]">{cal.era}</span>}
      <Button size="icon" variant="ghost" onClick={() => step(1)} data-testid="cal-next-button"><ChevronRight className="h-4 w-4" /></Button>
      <Button size="sm" variant="outline" onClick={() => setCursor({ year: cal.today.year, month: cal.today.month })} data-testid="cal-today-button">Today</Button>
    </div>
  );
}

function MonthView({ cal, cursor, onDay }) {
  const { data, error } = useCalMonth(cal.id, cursor.year, cursor.month);
  if (error) return <p className="text-sm text-red-400" data-testid="cal-month-error">{errMsg(error)}</p>;
  if (!data) return <div className="h-64 animate-pulse rounded-md bg-[#13171F]" />;
  const todayDay = cal.today.year === cursor.year && cal.today.month === cursor.month ? cal.today.day : null;
  return (
    <div className="space-y-3">
      {data.is_leap && <span className="text-xs uppercase tracking-widest text-[#F59E0B]" data-testid="cal-leap-badge">Leap year</span>}
      {data.year_events.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-[#C4B5FD]" data-testid="cal-year-timeline-events">
          <Clock className="h-4 w-4" />This year on the timeline: {data.year_events.map((e) => <span key={e.id} className="rounded bg-[#8B5CF6]/15 px-2 py-0.5">{e.title}</span>)}
        </div>
      )}
      <MonthGrid data={data} todayDay={todayDay} onDay={(d) => onDay({ ...d, year: data.year, month: data.month })} />
    </div>
  );
}

function YearView({ cal, cursor, onPickMonth }) {
  const { data, error } = useCalYear(cal.id, cursor.year, true);
  if (error) return <p className="text-sm text-red-400" data-testid="cal-year-error">{errMsg(error)}</p>;
  if (!data) return <div className="h-64 animate-pulse rounded-md bg-[#13171F]" />;
  return <YearGrid data={data} onPickMonth={onPickMonth} />;
}

export const CalendarsTab = ({ universe, canEdit, entities, timelines, onAddTimelineEvent, onOpenTimelineEvent }) => {
  const invalidate = useInvalidate();
  const { data: calendars = [], isLoading } = useCalendars(universe.id);
  const [cid, setCid] = useState("");
  const cal = calendars.find((c) => c.id === cid) || calendars[0];
  const { data: events = [] } = useCalEvents(cal?.id);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(null);
  const [day, setDay] = useState(null);
  const [evDialog, setEvDialog] = useState(null);
  const [calDialog, setCalDialog] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  useEffect(() => { if (cal) setCursor({ year: cal.today.year, month: cal.today.month }); }, [cal?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => invalidate(["cal"]);

  const remove = async () => {
    try {
      await api.delete(`/calendars/${cal.id}`);
      setCid("");
      refresh();
      toast.success("Calendar deleted");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDelOpen(false);
  };

  if (isLoading) return <div className="h-64 animate-pulse rounded-md bg-[#13171F]" />;
  const toolbar = canEdit && (
    <div className="flex flex-wrap gap-2">
      <Button variant="ghost" onClick={() => setCalDialog({})} data-testid="new-calendar-button"><Plus className="mr-1.5 h-4 w-4" />New calendar</Button>
      <Button variant="outline" className="border-[#F59E0B]/40 text-[#F59E0B]" onClick={() => setAiOpen(true)} data-testid="ai-calendar-button"><Sparkles className="mr-1.5 h-4 w-4" />Draft from lore</Button>
    </div>
  );
  return (
    <div data-testid="calendars-tab">
      {!cal ? (
        <div className="rounded-md border border-dashed border-[#2D3442] p-14 text-center" data-testid="calendars-empty">
          <p className="font-display text-3xl">No calendars yet.</p>
          <div className="mt-4 flex justify-center">{toolbar}</div>
        </div>
      ) : (
        <>
          <div className="mb-2 flex flex-wrap items-center gap-3">
            <Select value={cal.id} onValueChange={setCid}>
              <SelectTrigger className="h-11 w-72 font-display text-lg" data-testid="calendar-select"><SelectValue /></SelectTrigger>
              <SelectContent>{calendars.map((c) => <SelectItem key={c.id} value={c.id}><span className="mr-2 inline-block h-2 w-2 rounded-full" style={{ background: c.color }} />{c.name}</SelectItem>)}</SelectContent>
            </Select>
            {canEdit && (
              <>
                <Button size="icon" variant="ghost" onClick={() => setCalDialog({ initial: cal })} data-testid="edit-calendar-button"><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setDelOpen(true)} data-testid="delete-calendar-button"><Trash2 className="h-4 w-4 text-red-400" /></Button>
              </>
            )}
            <span className="text-sm text-[#F59E0B]" data-testid="calendar-today">Today: {cal.today.label}</span>
            <div className="ml-auto">{toolbar}</div>
          </div>
          <CalInfo cal={cal} timelines={timelines} />
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex rounded-md border border-[#2D3442] bg-[#13171F] p-1" data-testid="cal-view-switch">
              {VIEWS.map(([v, l]) => (
                <button key={v} type="button" onClick={() => setView(v)} data-testid={`cal-view-${v}`}
                  className={`rounded px-3 py-1.5 text-sm transition-colors ${view === v ? "bg-[#F59E0B]/15 text-[#F59E0B]" : "text-[#A8A090] hover:text-[#F3EBDD]"}`}>{l}</button>
              ))}
            </div>
            {cursor && ["month", "year"].includes(view) && <Nav cal={cal} cursor={cursor} setCursor={setCursor} view={view} />}
          </div>
          {cursor && view === "month" && <MonthView cal={cal} cursor={cursor} onDay={setDay} />}
          {cursor && view === "year" && <YearView cal={cal} cursor={cursor} onPickMonth={(m) => { setCursor({ ...cursor, month: m }); setView("month"); }} />}
          {view === "convert" && <ConverterPanel key={cal.id} uid={universe.id} calendars={calendars} initial={cal} />}
          {view === "intersect" && <IntersectionsPanel key={cal.id} uid={universe.id} calendars={calendars} base={cal} />}
          {["month", "year"].includes(view) && (
            <div className="mt-10 space-y-3">
              {canEdit && <Button size="sm" onClick={() => setEvDialog({})} data-testid="add-cal-event-button"><Plus className="mr-1 h-4 w-4" />Add recurring event</Button>}
              <CalEventList events={events} calendar={cal} onOpen={(e) => setEvDialog({ initial: e })} />
            </div>
          )}
        </>
      )}
      {day && (
        <DayDialog open onOpenChange={(o) => !o && setDay(null)} uid={universe.id} calendar={cal} day={day} canEdit={canEdit}
          onAddEvent={(d) => { setDay(null); setEvDialog({ initial: { recurrence: { type: "yearly", month: d.month, day: d.day, year: d.year } } }); }}
          onEditEvent={(id) => { const e = events.find((x) => x.id === id); if (e) { setDay(null); setEvDialog({ initial: e }); } }}
          onAddTimelineEvent={(p) => { setDay(null); onAddTimelineEvent(p); }}
          onOpenTimelineEvent={(tid, t) => { setDay(null); onOpenTimelineEvent(tid, t); }} />
      )}
      {evDialog && cal && (
        <CalEventDialog open onOpenChange={(o) => !o && setEvDialog(null)} calendar={cal} initial={evDialog.initial} entities={entities} onSaved={refresh} readOnly={!canEdit} />
      )}
      {calDialog && (
        <CalendarDialog open onOpenChange={(o) => !o && setCalDialog(null)} uid={universe.id} initial={calDialog.initial} draft={calDialog.draft} timelines={timelines}
          onSaved={(c) => { refresh(); setCid(c.id); toast.success("Calendar saved"); }} />
      )}
      {aiOpen && <AIDraftDialog open onOpenChange={setAiOpen} uid={universe.id} entities={entities} onDraft={(d) => setCalDialog({ draft: d })} />}
      <ConfirmDialog open={delOpen} onOpenChange={setDelOpen} onConfirm={remove} title={`Delete ${cal?.name}?`} description={`The calendar and all its recurring events will be removed.${cal?.kind === "gregorian" ? " The pre-installed Earth calendar will not be re-created." : ""}`} />
    </div>
  );
};
